/**
 * CareerTimeline — Interactive D3.js career timeline visualization.
 *
 * Renders a horizontal swimlane chart:
 *  - Each company/org gets its own lane
 *  - Roles appear as primary bars within lanes
 *  - Activities & projects appear as smaller items nested under roles
 *  - Education gets a separate "Education" lane
 *  - Standalone items (credentials, skills) appear in a "Milestones" lane
 *
 * Supports:
 *  - Pan & zoom (mouse wheel + drag)
 *  - Click on any item → fires onNodeClick with full node data
 *  - Responsive resize via ResizeObserver
 *  - Color-coded by node type
 */

import { useRef, useEffect, useCallback, useState } from 'react';
import * as d3 from 'd3';
import { GraphNodeBase, GraphEdge, GraphNodeType, TimeRange } from '../../types/graph';
import './CareerTimeline.css';

// ── Configuration ──────────────────────────────────────

const LANE_HEIGHT = 120;
const ROLE_BAR_HEIGHT = 36;
const ITEM_BAR_HEIGHT = 22;
const LANE_PADDING = 14;
const LANE_LABEL_WIDTH = 200;
const TOP_MARGIN = 55;
const BOTTOM_MARGIN = 30;
const MIN_BAR_WIDTH = 14;
const TOOLTIP_OFFSET = 12;

const TYPE_COLORS: Record<GraphNodeType, string> = {
  company: '#3b7dd8',
  organization: '#6358d4',
  role: '#27ae60',
  project: '#d35400',
  activity: '#c0392b',
  knowledgeItem: '#8e44ad',
  credential: '#d4a017',
  educationRecord: '#16a085',
  outcome: '#2980b9',
  evidence: '#7f8c8d',
};

// Lighter fills for bars (better label contrast)
const TYPE_FILLS: Record<GraphNodeType, string> = {
  company: '#5b9be6',
  organization: '#8678ee',
  role: '#52c77d',
  project: '#e8874b',
  activity: '#e05a4e',
  knowledgeItem: '#a569bd',
  credential: '#f0c040',
  educationRecord: '#48c9a9',
  outcome: '#5dade2',
  evidence: '#aab7b8',
};

const TYPE_EMOJI: Record<GraphNodeType, string> = {
  company: '🏢',
  organization: '🏛️',
  role: '💼',
  project: '📁',
  activity: '⚡',
  knowledgeItem: '🧠',
  credential: '📜',
  educationRecord: '🎓',
  outcome: '📊',
  evidence: '📎',
};

// ── Types ──────────────────────────────────────────────

interface TimelineItem {
  node: GraphNodeBase;
  start: Date;
  end: Date;
  laneKey: string;        // company/org label, "Education", or "Milestones"
  tier: 'role' | 'item';  // roles get bigger bars
}

interface Lane {
  key: string;
  emoji: string;
  items: TimelineItem[];
  y: number;
  height: number;
}

interface Props {
  nodes: GraphNodeBase[];
  edges: GraphEdge[];
  onNodeClick?: (node: GraphNodeBase) => void;
  selectedNodeId?: string;
}

// ── Helpers ────────────────────────────────────────────

function parseDate(str?: string): Date | null {
  if (!str) return null;
  if (str.toLowerCase() === 'present') return new Date();

  // Parse as local date to avoid timezone shift issues
  // e.g. new Date('2020-01-01') is UTC midnight → Dec 31 in EST
  const parts = str.match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/);
  if (parts) {
    const year = parseInt(parts[1], 10);
    const month = parts[2] ? parseInt(parts[2], 10) - 1 : 0;
    const day = parts[3] ? parseInt(parts[3], 10) : 1;
    return new Date(year, month, day);
  }

  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function getTimeRange(node: GraphNodeBase): { start: Date; end: Date } | null {
  const tr = node.timeRange as TimeRange | undefined;
  if (!tr) return null;
  const start = parseDate(tr.startDate);
  if (!start) return null;
  const end = parseDate(tr.endDate) || new Date();
  // Ensure at least 1 month visible
  const minEnd = new Date(start);
  minEnd.setMonth(minEnd.getMonth() + 1);
  return { start, end: end > minEnd ? end : minEnd };
}

/** Resolve which company/org a role belongs to via edges. */
function resolveCompanyForRole(
  roleId: string,
  edges: GraphEdge[],
  nodeMap: Map<string, GraphNodeBase>
): GraphNodeBase | null {
  for (const e of edges) {
    if (e.relationType === 'ROLE_AT_COMPANY' && e.fromId === roleId) {
      return nodeMap.get(e.toId) || null;
    }
  }
  return null;
}

/** Resolve which role an activity/project belongs to. */
function resolveRoleForItem(
  itemId: string,
  edges: GraphEdge[],
  nodeMap: Map<string, GraphNodeBase>
): GraphNodeBase | null {
  for (const e of edges) {
    if (
      (e.relationType === 'ACTIVITY_BELONGS_TO_ROLE' ||
        e.relationType === 'PROJECT_HAS_ACTIVITY') &&
      e.fromId === itemId
    ) {
      return nodeMap.get(e.toId) || null;
    }
  }
  return null;
}

function truncate(text: string, maxLen: number): string {
  return text.length > maxLen ? text.slice(0, maxLen - 1) + '…' : text;
}

// ── Component ──────────────────────────────────────────

export default function CareerTimeline({ nodes, edges, onNodeClick, selectedNodeId }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  const buildTimeline = useCallback(() => {
    if (!svgRef.current || !containerRef.current) return;

    const nodeMap = new Map<string, GraphNodeBase>();
    for (const n of nodes) nodeMap.set(n.id, n);

    // ── Build timeline items ──
    const items: TimelineItem[] = [];

    // 1. Roles → primary bars in their company lane
    const roleToLane = new Map<string, string>();
    for (const node of nodes) {
      if (node.nodeType !== 'role') continue;
      const range = getTimeRange(node);
      if (!range) continue;

      const company = resolveCompanyForRole(node.id, edges, nodeMap);
      const laneKey = company?.canonicalLabel || 'Other Roles';

      roleToLane.set(node.id, laneKey);
      items.push({ node, ...range, laneKey, tier: 'role' });
    }

    // 2. Activities & projects → smaller bars under the role's company lane
    for (const node of nodes) {
      if (node.nodeType !== 'activity' && node.nodeType !== 'project') continue;
      const range = getTimeRange(node);
      if (!range) continue;

      const role = resolveRoleForItem(node.id, edges, nodeMap);
      const laneKey = role ? (roleToLane.get(role.id) || 'Other') : 'Independent';
      items.push({ node, ...range, laneKey, tier: 'item' });
    }

    // 3. Education records → "Education" lane
    for (const node of nodes) {
      if (node.nodeType !== 'educationRecord') continue;
      const range = getTimeRange(node);
      if (!range) continue;
      items.push({ node, ...range, laneKey: '🎓 Education', tier: 'role' });
    }

    // 4. Credentials with dates → "Milestones" lane
    for (const node of nodes) {
      if (node.nodeType !== 'credential') continue;
      const range = getTimeRange(node);
      if (!range) continue;
      items.push({ node, ...range, laneKey: '📜 Milestones', tier: 'item' });
    }

    if (items.length === 0) {
      setIsEmpty(true);
      return;
    }
    setIsEmpty(false);

    // ── Build lanes ──
    const laneMap = new Map<string, TimelineItem[]>();
    for (const item of items) {
      if (!laneMap.has(item.laneKey)) laneMap.set(item.laneKey, []);
      laneMap.get(item.laneKey)!.push(item);
    }

    // Sort lanes: company lanes by earliest start date, then Education, then Milestones
    const laneEntries = Array.from(laneMap.entries()).sort(([aKey, aItems], [bKey, bItems]) => {
      // Special lanes go last
      const aSpecial = aKey.startsWith('🎓') ? 1 : aKey.startsWith('📜') ? 2 : aKey === 'Independent' ? 3 : 0;
      const bSpecial = bKey.startsWith('🎓') ? 1 : bKey.startsWith('📜') ? 2 : bKey === 'Independent' ? 3 : 0;
      if (aSpecial !== bSpecial) return aSpecial - bSpecial;
      // Within company lanes, sort by earliest start
      const aMin = Math.min(...aItems.map((i) => i.start.getTime()));
      const bMin = Math.min(...bItems.map((i) => i.start.getTime()));
      return aMin - bMin;
    });

    const lanes: Lane[] = [];
    let currentY = TOP_MARGIN;
    for (const [key, laneItems] of laneEntries) {
      const roleCount = laneItems.filter((i) => i.tier === 'role').length;
      const itemCount = laneItems.filter((i) => i.tier === 'item').length;
      const height = Math.max(
        LANE_HEIGHT,
        LANE_PADDING * 2 + roleCount * (ROLE_BAR_HEIGHT + 4) + itemCount * (ITEM_BAR_HEIGHT + 3)
      );
      const emoji = key.startsWith('🎓') ? '🎓' : key.startsWith('📜') ? '📜' : '🏢';
      lanes.push({ key, emoji, items: laneItems, y: currentY, height });
      currentY += height;
    }

    const totalHeight = currentY + BOTTOM_MARGIN;

    // ── Time scale ──
    const timeExtent = d3.extent(items.flatMap((i) => [i.start, i.end])) as [Date, Date];
    // Add 6 months padding on each side
    const padMs = 6 * 30 * 24 * 60 * 60 * 1000;
    const domainStart = new Date(timeExtent[0].getTime() - padMs);
    const domainEnd = new Date(timeExtent[1].getTime() + padMs);

    const containerWidth = containerRef.current.clientWidth;
    const chartWidth = Math.max(containerWidth - LANE_LABEL_WIDTH, 600);

    const xScale = d3.scaleTime()
      .domain([domainStart, domainEnd])
      .range([0, chartWidth]);

    // ── Clear & setup SVG ──
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', containerWidth).attr('height', totalHeight);

    // Clip path for chart area
    svg.append('defs')
      .append('clipPath')
      .attr('id', 'chart-clip')
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', chartWidth)
      .attr('height', totalHeight);

    // ── Lane labels (fixed left) ──
    const labelGroup = svg.append('g').attr('class', 'lane-labels');
    for (const lane of lanes) {
      // Lane background
      labelGroup.append('rect')
        .attr('x', 0)
        .attr('y', lane.y)
        .attr('width', LANE_LABEL_WIDTH)
        .attr('height', lane.height)
        .attr('class', 'lane-label-bg');

      // Lane border (bottom)
      labelGroup.append('line')
        .attr('x1', 0)
        .attr('x2', LANE_LABEL_WIDTH)
        .attr('y1', lane.y + lane.height)
        .attr('y2', lane.y + lane.height)
        .attr('class', 'lane-border');

      // Label text
      const displayKey = lane.key.replace(/^[🎓📜🏢]\s*/, '');
      labelGroup.append('text')
        .attr('x', 14)
        .attr('y', lane.y + 26)
        .attr('class', 'lane-label-text')
        .text(`${lane.emoji} ${truncate(displayKey, 20)}`);

      // Item count badge
      labelGroup.append('text')
        .attr('x', 14)
        .attr('y', lane.y + 44)
        .attr('class', 'lane-label-count')
        .text(`${lane.items.length} item${lane.items.length !== 1 ? 's' : ''}`);
    }

    // ── Chart area (scrollable/zoomable) ──
    const chartGroup = svg.append('g')
      .attr('transform', `translate(${LANE_LABEL_WIDTH}, 0)`);

    chartGroup.append('rect')
      .attr('width', chartWidth)
      .attr('height', totalHeight)
      .attr('fill', 'transparent')
      .attr('class', 'chart-bg');

    const zoomGroup = chartGroup.append('g')
      .attr('clip-path', 'url(#chart-clip)');

    // ── Draw lane stripes ──
    for (let i = 0; i < lanes.length; i++) {
      const lane = lanes[i];
      zoomGroup.append('rect')
        .attr('x', 0)
        .attr('y', lane.y)
        .attr('width', chartWidth)
        .attr('height', lane.height)
        .attr('class', `lane-stripe ${i % 2 === 0 ? 'even' : 'odd'}`);

      // Lane divider line
      zoomGroup.append('line')
        .attr('x1', 0)
        .attr('x2', chartWidth)
        .attr('y1', lane.y + lane.height)
        .attr('y2', lane.y + lane.height)
        .attr('class', 'lane-divider');
    }

    // ── Time axis ──
    // Calculate span to pick appropriate tick interval
    const spanYears = (domainEnd.getTime() - domainStart.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    const tickInterval = spanYears > 15 ? d3.timeYear.every(2) : spanYears > 6 ? d3.timeYear.every(1) : d3.timeMonth.every(6);
    const tickFormat = spanYears > 6
      ? (d: Date | d3.NumberValue) => d3.timeFormat('%Y')(d as Date)
      : (d: Date | d3.NumberValue) => d3.timeFormat('%b %Y')(d as Date);

    const xAxis = d3.axisTop(xScale)
      .ticks(tickInterval)
      .tickFormat(tickFormat);

    const axisGroup = zoomGroup.append('g')
      .attr('class', 'time-axis')
      .attr('transform', `translate(0, ${TOP_MARGIN})`)
      .call(xAxis);

    // Year grid lines
    const yearTicks = xScale.ticks(d3.timeYear.every(1)!);
    zoomGroup.append('g')
      .attr('class', 'grid-lines')
      .selectAll('line')
      .data(yearTicks)
      .join('line')
      .attr('x1', (d) => xScale(d))
      .attr('x2', (d) => xScale(d))
      .attr('y1', TOP_MARGIN)
      .attr('y2', totalHeight - BOTTOM_MARGIN)
      .attr('class', 'grid-line');

    // Half-year grid lines (lighter) for shorter timespans
    if (spanYears <= 6) {
      const halfYearTicks = xScale.ticks(d3.timeMonth.every(6)!);
      zoomGroup.append('g')
        .attr('class', 'grid-lines-minor')
        .selectAll('line')
        .data(halfYearTicks)
        .join('line')
        .attr('x1', (d) => xScale(d))
        .attr('x2', (d) => xScale(d))
        .attr('y1', TOP_MARGIN)
        .attr('y2', totalHeight - BOTTOM_MARGIN)
        .attr('class', 'grid-line-minor');
    }

    // "Now" line
    const now = new Date();
    if (now >= domainStart && now <= domainEnd) {
      zoomGroup.append('line')
        .attr('x1', xScale(now))
        .attr('x2', xScale(now))
        .attr('y1', TOP_MARGIN)
        .attr('y2', totalHeight - BOTTOM_MARGIN)
        .attr('class', 'now-line');

      zoomGroup.append('text')
        .attr('x', xScale(now))
        .attr('y', TOP_MARGIN - 6)
        .attr('text-anchor', 'middle')
        .attr('class', 'now-label')
        .text('Now');
    }

    // ── Draw items ──
    const tooltip = d3.select(tooltipRef.current);

    for (const lane of lanes) {
      // Track offsets within lane for stacking
      let roleOffset = 0;
      let itemOffset = 0;

      // Sort: roles first (by start date), then items
      const sorted = [...lane.items].sort((a, b) => {
        if (a.tier !== b.tier) return a.tier === 'role' ? -1 : 1;
        return a.start.getTime() - b.start.getTime();
      });

      for (const item of sorted) {
        const x = xScale(item.start);
        const rawWidth = xScale(item.end) - xScale(item.start);
        const width = Math.max(rawWidth, MIN_BAR_WIDTH);
        const isRole = item.tier === 'role';
        const barHeight = isRole ? ROLE_BAR_HEIGHT : ITEM_BAR_HEIGHT;
        const yOff = isRole
          ? lane.y + LANE_PADDING + roleOffset * (ROLE_BAR_HEIGHT + 4)
          : lane.y + LANE_PADDING + lane.items.filter((i) => i.tier === 'role').length * (ROLE_BAR_HEIGHT + 4) + itemOffset * (ITEM_BAR_HEIGHT + 3);

        if (isRole) roleOffset++;
        else itemOffset++;

        const color = TYPE_COLORS[item.node.nodeType];
        const isSelected = item.node.id === selectedNodeId;

        const bar = zoomGroup.append('g')
          .attr('class', `timeline-bar ${isSelected ? 'selected' : ''}`)
          .attr('cursor', 'pointer');

        const fill = TYPE_FILLS[item.node.nodeType] || color;

        // Bar rectangle with border for contrast
        bar.append('rect')
          .attr('x', x)
          .attr('y', yOff)
          .attr('width', width)
          .attr('height', barHeight)
          .attr('rx', isRole ? 6 : 4)
          .attr('fill', fill)
          .attr('stroke', isSelected ? '#fff' : color)
          .attr('stroke-width', isSelected ? 2.5 : 1);

        // Bar label — always show for roles, for items when wide enough
        const labelText = item.node.title || item.node.name || item.node.canonicalLabel;
        const tr = item.node.timeRange;
        const dateLabel = tr ? ` (${tr.startDate || '?'}–${tr.endDate || 'now'})` : '';
        const fullLabel = labelText + dateLabel;

        if (isRole || width > 40) {
          // Clip text to fit bar
          const maxChars = Math.max(4, Math.floor(width / (isRole ? 7 : 6.5)));
          bar.append('text')
            .attr('x', x + 6)
            .attr('y', yOff + barHeight / 2)
            .attr('dy', '0.35em')
            .attr('class', `bar-label ${isRole ? 'role-label' : 'item-label'}`)
            .text(truncate(fullLabel, maxChars));
        }

        // For narrow bars, add a small date label below
        if (!isRole && width <= 40 && width >= MIN_BAR_WIDTH) {
          bar.append('text')
            .attr('x', x)
            .attr('y', yOff + barHeight + 10)
            .attr('class', 'bar-date-label')
            .text(truncate(labelText, 12));
        }

        // Hover tooltip
        bar.on('mouseenter', () => {
          const tr = item.node.timeRange;
          const dateStr = tr
            ? `${tr.startDate || '?'} → ${tr.endDate || 'Present'}`
            : '';
          tooltip
            .style('opacity', '1')
            .html(`
              <div class="tt-header">${TYPE_EMOJI[item.node.nodeType]} <strong>${item.node.title || item.node.name || item.node.canonicalLabel}</strong></div>
              <div class="tt-type">${item.node.nodeType}</div>
              ${dateStr ? `<div class="tt-date">${dateStr}</div>` : ''}
              ${item.node.description ? `<div class="tt-desc">${truncate(item.node.description, 120)}</div>` : ''}
              <div class="tt-hint">Click for details</div>
            `);
        })
        .on('mousemove', (event: MouseEvent) => {
          const e = event as unknown as {offsetX: number; offsetY: number};
          tooltip
            .style('left', `${e.offsetX + TOOLTIP_OFFSET}px`)
            .style('top', `${e.offsetY + TOOLTIP_OFFSET}px`);
        })
        .on('mouseleave', () => {
          tooltip.style('opacity', '0');
        });

        // Click handler
        bar.on('click', () => {
          onNodeClick?.(item.node);
        });
      }
    }

    // ── Zoom behavior ──
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 8])
      .translateExtent([[0, 0], [chartWidth * 3, totalHeight]])
      .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
        const newX = event.transform.rescaleX(xScale);
        zoomGroup.attr('transform', `translate(${event.transform.x}, 0) scale(${event.transform.k}, 1)`);

        // Calculate dynamic ticks based on visible span
        const visibleDomain = newX.domain();
        const visSpan = (visibleDomain[1].getTime() - visibleDomain[0].getTime()) / (365.25 * 24 * 60 * 60 * 1000);
        const zTickInterval = visSpan > 15 ? d3.timeYear.every(2) : visSpan > 6 ? d3.timeYear.every(1) : d3.timeMonth.every(6);
        const zTickFormat = visSpan > 6
          ? (d: Date | d3.NumberValue) => d3.timeFormat('%Y')(d as Date)
          : (d: Date | d3.NumberValue) => d3.timeFormat('%b %Y')(d as Date);

        const newAxis = d3.axisTop(newX)
          .ticks(zTickInterval)
          .tickFormat(zTickFormat);
        axisGroup.call(newAxis);
      });

    svg.call(zoom);

    // Double-click to reset zoom
    svg.on('dblclick.zoom', () => {
      svg.transition().duration(500).call(zoom.transform, d3.zoomIdentity);
    });

  }, [nodes, edges, onNodeClick, selectedNodeId]);

  // Build on mount and when data changes
  useEffect(() => {
    buildTimeline();
  }, [buildTimeline]);

  // Rebuild on container resize
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => buildTimeline());
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [buildTimeline]);

  return (
    <div className="career-timeline" ref={containerRef} data-testid="career-timeline">
      {isEmpty && (
        <div className="career-timeline-empty">
          No time-based data available. Ingest a resume to populate the timeline.
        </div>
      )}
      <div className="career-timeline-controls">
        <span className="timeline-hint">Scroll to zoom · Drag to pan · Double-click to reset · Click items for details</span>
      </div>
      <div className="career-timeline-canvas" style={{ position: 'relative', overflow: 'hidden' }}>
        <svg ref={svgRef} />
        <div ref={tooltipRef} className="timeline-tooltip" />
      </div>
      <div className="timeline-legend">
        {(['role', 'activity', 'project', 'educationRecord', 'credential'] as GraphNodeType[]).map((t) => (
          <span key={t} className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: TYPE_COLORS[t] }} />
            {TYPE_EMOJI[t]} {t === 'educationRecord' ? 'Education' : t === 'knowledgeItem' ? 'Knowledge' : t.charAt(0).toUpperCase() + t.slice(1)}s
          </span>
        ))}
      </div>
    </div>
  );
}
